import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from '../utils/axios';
import { AuthContext } from '../context/AuthContext';
import './styles/UserManager.css';

export default function UserManager() {
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [masterSites, setMasterSites] = useState([]);
  const [form, setForm] = useState({
    username: '',
    password: '',
    role: 'user',
    masterSiteAccess: []
  });

  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('token');
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchUsers();
      fetchMasterSites();
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/users', authHeader);
      setUsers(res.data || []);
    } catch (error) {
      alert('Error fetching users: ' + error.message);
    }
  };

  const fetchMasterSites = async () => {
    try {
      const res = await axios.get('/mastersites', authHeader);
      setMasterSites(res.data || []);
    } catch (e) {
      // optional: toast
    }
  };

  const resetForm = () => {
    setForm({ username: '', password: '', role: 'user', masterSiteAccess: [] });
    setEditingId(null);
    setPhotoFile(null);
    setPhotoPreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        // Update access (and optionally upload a new photo)
        await axios.put(
          `/users/${editingId}/access`,
          { masterSiteIds: form.masterSiteAccess },
          authHeader
        );

        if (photoFile) {
          await uploadPhoto(editingId);
        }

        setMessage('✅ User updated');
      } else {
        // Create user first
        await axios.post('/users/register', form, authHeader);
        setMessage('✅ User created');

        // If a photo is chosen, find the new user's _id and upload the photo
        if (photoFile) {
          // Re-fetch users and find by username (assuming unique username)
          const res = await axios.get('/users', authHeader);
          const created = (res.data || []).find((u) => u.username === form.username);
          if (created?._id) {
            await uploadPhoto(created._id);
          }
        }
      }

      resetForm();
      fetchUsers();
    } catch (err) {
      setMessage('❌ Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const uploadPhoto = async (userId) => {
    const fd = new FormData();
    fd.append('profilePic', photoFile);
    await axios.put(`/users/${userId}/profile-pic`, fd, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
  };

  const handleEdit = (u) => {
    setEditingId(u._id);
    setForm({
      username: u.username,
      password: '',
      role: u.role,
      masterSiteAccess: Array.isArray(u.masterSiteAccess)
        ? u.masterSiteAccess.map((ms) => ms._id || ms)
        : []
    });
    setPhotoFile(null);
    setPhotoPreview(u.profilePic || '');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await axios.delete(`/users/${id}`, authHeader);
      fetchUsers();
    } catch (e) {
      setMessage('❌ Error deleting user: ' + (e.response?.data?.message || e.message));
    }
  };

  const handleMultiSelectChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, (option) => option.value);
    setForm((prev) => ({ ...prev, masterSiteAccess: selected }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setPhotoFile(file || null);
    if (file) {
      const url = URL.createObjectURL(file);
      setPhotoPreview(url);
    } else {
      setPhotoPreview('');
    }
  };

  // Guards
  if (!user) return <div>🔄 Loading user info...</div>;
  if (user.role !== 'admin') return <div>⛔ Access Denied: Admins Only</div>;

  return (
    <div className="user-manager-container">
      <h2>{editingId ? 'Edit User' : 'Create User'}</h2>
      {message && <div className="message-box">{message}</div>}

      <form onSubmit={handleSubmit} className="user-form" encType="multipart/form-data">
        <div className="form-row">
          <div className="col">
            <label>Username</label>
            <input
              type="text"
              placeholder="Username"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              required
              disabled={!!editingId}
            />
          </div>

          {!editingId && (
            <div className="col">
              <label>Password</label>
              <input
                type="password"
                placeholder="Password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
              />
            </div>
          )}

          <div className="col">
            <label>Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          {form.role !== 'admin' && (
            <div className="col">
              <label>Master Sites</label>
              <select
                multiple
                value={form.masterSiteAccess}
                onChange={handleMultiSelectChange}
                className="multi-select"
              >
                {masterSites.map((ms) => (
                  <option key={ms._id} value={ms._id}>
                    {ms.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="col">
            <label>Profile Photo</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
            {photoPreview && (
              <div className="photo-preview">
                <img src={photoPreview} alt="preview" />
              </div>
            )}
          </div>
        </div>

        <div className="actions">
          <button type="submit">{editingId ? 'Save Changes' : 'Create User'}</button>
          {editingId && (
            <button type="button" className="secondary" onClick={resetForm}>
              Cancel
            </button>
          )}
        </div>
      </form>

      <h3>Existing Users</h3>
      <table className="user-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Role</th>
            <th>Master Sites</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u._id}>
              <td>
                <div className="user-cell">
                  <img
                    className="avatar"
                    src={
                      u.profilePic
                        ? u.profilePic
                        : 'https://ui-avatars.com/api/?name=' + encodeURIComponent(u.username)
                    }
                    alt={u.username}
                  />
                  <span>{u.username}</span>
                </div>
              </td>
              <td>{u.role}</td>
              <td>
                {Array.isArray(u.masterSiteAccess)
                  ? u.masterSiteAccess.map((ms) => ms.name || ms).join(', ')
                  : ''}
              </td>
              <td>
                <button onClick={() => handleEdit(u)}>Edit</button>
                <button onClick={() => handleDelete(u._id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
